import {
  Activity,
  AlertCircle,
  Bell,
  CheckCircle,
  Clock,
  Download,
  Edit,
  Eye,
  FileText,
  Globe,
  Key,
  Lock,
  Mail,
  Phone,
  Shield,
  Trash2,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import api from "@/services/api";

interface FormData {
  confirmation: boolean;
  email: string;
  identity_verification_file: File | null;
  phone: string;
  priority: string;
  request_details: string;
  request_type: string;
}

interface FormErrors {
  [key: string]: string[];
}

export default function DataProtection() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({
    confirmation: false,
    email: "",
    identity_verification_file: null,
    phone: "",
    priority: "normal",
    request_details: "",
    request_type: "",
  });

  useEffect(() => {
    if (showSuccessToast) {
      const timer = setTimeout(() => {
        setShowSuccessToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessToast]);

  useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => {
        setShowErrorToast(false);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [showErrorToast]);

  const tabs = [
    { icon: <Shield size={16} />, id: "overview", label: "Overview" },
    { icon: <UserCheck size={16} />, id: "your-rights", label: "Your Rights" },
    { icon: <Lock size={16} />, id: "security", label: "Security" },
    { icon: <FileText size={16} />, id: "requests", label: "Data Requests" },
  ];

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.request_type) {
      errors.request_type = ["The request type field is required."];
    }

    if (!formData.priority) {
      errors.priority = ["The priority field is required."];
    }

    if (!formData.email) {
      errors.email = ["The email field is required."];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = ["Please enter a valid email address."];
    } else if (formData.email.length > 255) {
      errors.email = ["Email must not exceed 255 characters."];
    }

    if (!formData.phone) {
      errors.phone = ["The phone field is required."];
    } else if (formData.phone.length > 20) {
      errors.phone = ["Phone must not exceed 20 characters."];
    }

    if (!formData.request_details) {
      errors.request_details = ["The request details field is required."];
    } else if (formData.request_details.length > 2000) {
      errors.request_details = [
        "Request details must not exceed 2000 characters.",
      ];
    }

    if (!formData.identity_verification_file) {
      errors.identity_verification_file = [
        "The identity verification file is required.",
      ];
    } else {
      const file = formData.identity_verification_file;
      const validTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!validTypes.includes(file.type)) {
        errors.identity_verification_file = ["File must be PDF, JPG, or PNG."];
      }
      if (file.size > 5 * 1024 * 1024) {
        errors.identity_verification_file = ["File must not exceed 5MB."];
      }
    }

    if (!formData.confirmation) {
      errors.confirmation = ["You must confirm this request."];
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, value } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      identity_verification_file: file,
    }));

    if (formErrors.identity_verification_file) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.identity_verification_file;
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("request_type", formData.request_type);
      formDataToSend.append("priority", formData.priority);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("request_details", formData.request_details);
      formDataToSend.append("confirmation", String(formData.confirmation));

      if (formData.identity_verification_file) {
        formDataToSend.append(
          "identity_verification_file",
          formData.identity_verification_file,
        );
      }

      const response = await api.post(
        "/data-protection/submit",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowSuccessToast(true);
        setFormData({
          confirmation: false,
          email: "",
          identity_verification_file: null,
          phone: "",
          priority: "normal",
          request_details: "",
          request_type: "",
        });
        setFormErrors({});

        // Reset file input
        const fileInput = document.querySelector(
          'input[name="identity_verification_file"]',
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
        setErrorMessage(
          error.response.data.message ||
            "Please fix the errors below and try again.",
        );
      } else if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage(
          "An error occurred while submitting your request. Please try again.",
        );
      }
      setShowErrorToast(true);
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const dataRights = [
    {
      description: "Request a copy of the personal data we hold about you",
      icon: <Eye className="text-primary" size={24} />,
      right: "Right of Access",
      timeframe: "30 days",
    },
    {
      description: "Correct inaccurate or incomplete personal data",
      icon: <Edit className="text-secondary" size={24} />,
      right: "Right to Rectification",
      timeframe: "30 days",
    },
    {
      description:
        "Request deletion of your personal data under certain conditions",
      icon: <Trash2 className="text-error" size={24} />,
      right: "Right to Erasure",
      timeframe: "30 days",
    },
    {
      description: "Receive your data in a structured, machine-readable format",
      icon: <Download className="text-success" size={24} />,
      right: "Right to Data Portability",
      timeframe: "30 days",
    },
  ];

  const securityMeasures = [
    {
      category: "Encryption",
      measures: [
        "Data encrypted in transit using TLS/SSl",
        "Data encrypted at rest using AES-256",
        "End-to-end encryption for sensitive communications",
        "Regular encryption key rotation",
      ],
    },
    {
      category: "Access Controls",
      measures: [
        "Multi-factor authentication for all systems",
        "Role-based access permissions",
        "Regular access reviews and audits",
        "Automated account deactivation procedures",
      ],
    },
    {
      category: "Monitoring & Incident Response",
      measures: [
        "24/7 security monitoring and alerting",
        "Automated threat detection systems",
        "Incident response team and procedures",
        "Regular security assessments and penetration testing",
      ],
    },
    {
      category: "Data Governance",
      measures: [
        "Data minimization and purpose limitation",
        "Regular data retention reviews",
        "Privacy impact assessments for new features",
        "Staff training on data protection principles",
      ],
    },
  ];

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Data Protection Act Compliance */}
      <div className="card border border-success/20 bg-success/5">
        <div className="card-body">
          <div className="mb-4 flex items-center gap-3">
            <CheckCircle className="text-success" size={24} />
            <h3 className="text-xl font-semibold">
              Data Protection Act Compliance
            </h3>
          </div>
          <p className="mb-4 text-base-content/80">
            Hareeng is fully compliant with Africas Data Protection Act. We are
            committed to protecting your personal information and ensuring it is
            processed lawfully, fairly, and transparently.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="stat bg-base-100">
              <div className="stat-title">Compliance Status</div>
              <div className="stat-value text-lg text-success">✓ Compliant</div>
            </div>
            <div className="stat bg-base-100">
              <div className="stat-title">Registration</div>
              <div className="stat-value text-lg text-primary">Registered</div>
            </div>
            <div className="stat bg-base-100">
              <div className="stat-title">Last Audit</div>
              <div className="stat-value text-lg text-info">{new Date().getFullYear()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Processing Principles */}
      <div>
        <h3 className="mb-4 text-xl font-semibold">
          Our Data Processing Principles
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <h4 className="mb-2 font-semibold">Lawfulness & Transparency</h4>
              <p className="text-sm text-base-content/70">
                We process data lawfully and transparently, with clear
                communication about how and why we use your information.
              </p>
            </div>
          </div>

          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <h4 className="mb-2 font-semibold">Purpose Limitation</h4>
              <p className="text-sm text-base-content/70">
                We collect data for specific, legitimate purposes and do not use
                it for unrelated activities.
              </p>
            </div>
          </div>

          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <h4 className="mb-2 font-semibold">Data Minimization</h4>
              <p className="text-sm text-base-content/70">
                We only collect and process the minimum amount of data necessary
                for our services.
              </p>
            </div>
          </div>

          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <h4 className="mb-2 font-semibold">Accuracy & Security</h4>
              <p className="text-sm text-base-content/70">
                We ensure data accuracy and implement robust security measures
                to protect your information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Categories */}
      <div>
        <h3 className="mb-4 text-xl font-semibold">Types of Data We Process</h3>
        <div className="space-y-4">
          <div className="collapse collapse-arrow bg-base-100">
            <input defaultChecked name="data-accordion" type="radio" />
            <div className="collapse-title text-lg font-medium">
              Personal Identifiers
            </div>
            <div className="collapse-content">
              <p className="mb-2 text-sm text-base-content/70">
                Information that directly identifies you as an individual.
              </p>
              <ul className="space-y-1 text-sm">
                <li>• Full name and contact details</li>
                <li>• Email address and phone number</li>
                <li>• Government ID numbers (for verification)</li>
                <li>• Profile photographs</li>
              </ul>
            </div>
          </div>

          <div className="collapse collapse-arrow bg-base-100">
            <input name="data-accordion" type="radio" />
            <div className="collapse-title text-lg font-medium">
              Location Data
            </div>
            <div className="collapse-content">
              <p className="mb-2 text-sm text-base-content/70">
                Information about your location when using our services.
              </p>
              <ul className="space-y-1 text-sm">
                <li>• GPS coordinates for pickup/dropoff</li>
                <li>• Trip routes and destinations</li>
                <li>• Location history (with consent)</li>
                <li>• Approximate location from IP address</li>
              </ul>
            </div>
          </div>

          <div className="collapse collapse-arrow bg-base-100">
            <input name="data-accordion" type="radio" />
            <div className="collapse-title text-lg font-medium">
              Usage & Device Data
            </div>
            <div className="collapse-content">
              <p className="mb-2 text-sm text-base-content/70">
                Technical information about how you use our platform.
              </p>
              <ul className="space-y-1 text-sm">
                <li>• App usage patterns and preferences</li>
                <li>• Device type, operating system, app version</li>
                <li>• IP address and browser information</li>
                <li>• Crash reports and performance data</li>
              </ul>
            </div>
          </div>

          <div className="collapse collapse-arrow bg-base-100">
            <input name="data-accordion" type="radio" />
            <div className="collapse-title text-lg font-medium">
              Transaction Data
            </div>
            <div className="collapse-content">
              <p className="mb-2 text-sm text-base-content/70">
                Information related to your transactions and payments.
              </p>
              <ul className="space-y-1 text-sm">
                <li>• Trip and order history</li>
                <li>• Payment method details (encrypted)</li>
                <li>• Billing and invoice information</li>
                <li>• Refund and dispute records</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderYourRights = () => (
    <div className="space-y-8">
      <div className="alert alert-info">
        <AlertCircle size={20} />
        <span>
          Under the Data Protection Act, you have specific rights regarding your
          personal data. These rights are fundamental to data protection and
          privacy.
        </span>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {dataRights.map((right, index) => (
          <div className="card bg-base-100 shadow-lg" key={index}>
            <div className="card-body">
              <div className="mb-3 flex items-center gap-3">
                {right.icon}
                <h3 className="card-title text-lg">{right.right}</h3>
              </div>
              <p className="mb-4 text-base-content/80">{right.description}</p>

              <div className="flex items-center justify-between text-sm">
                <span className="text-base-content/70">
                  Response time: {right.timeframe}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Rights */}
      <div>
        <h3 className="mb-4 text-xl font-semibold">Additional Rights</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="card border border-warning/20 bg-warning/5">
            <div className="card-body">
              <h4 className="mb-2 font-semibold">Right to Object</h4>
              <p className="mb-3 text-sm text-base-content/70">
                Object to processing of your data for direct marketing or other
                specific purposes.
              </p>
            </div>
          </div>

          <div className="card border border-info/20 bg-info/5">
            <div className="card-body">
              <h4 className="mb-2 font-semibold">
                Right to Restrict Processing
              </h4>
              <p className="mb-3 text-sm text-base-content/70">
                Request to limit how we process your data under certain
                circumstances.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rights Exercise Process */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h3 className="card-title mb-4">How to Exercise Your Rights</h3>
          <div className="steps steps-vertical lg:steps-horizontal">
            <div className="step step-primary">
              <div className="text-sm">
                <div className="font-semibold">Submit Request</div>
                <div>Use our online form or email legal@hareeng.org</div>
              </div>
            </div>
            <div className="step step-primary">
              <div className="text-sm">
                <div className="font-semibold">Identity Verification</div>
                <div>We verify your identity for security purposes</div>
              </div>
            </div>
            <div className="step step-primary">
              <div className="text-sm">
                <div className="font-semibold">Processing</div>
                <div>We review and process your request within 30 days</div>
              </div>
            </div>
            <div className="step step-primary">
              <div className="text-sm">
                <div className="font-semibold">Response</div>
                <div>You receive confirmation and requested information</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-8">
      <div className="alert alert-success">
        <Shield size={20} />
        <span>
          Your data security is our top priority. We implement industry-leading
          security measures to protect your personal information.
        </span>
      </div>

      {/* Security Overview */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="stat bg-base-100 shadow-lg">
          <div className="stat-figure text-primary">
            <Lock size={32} />
          </div>
          <div className="stat-title">Encryption Level</div>
          <div className="stat-value text-primary">AES-256</div>
          <div className="stat-desc">Military-grade encryption</div>
        </div>

        <div className="stat bg-base-100 shadow-lg">
          <div className="stat-figure text-success">
            <Activity size={32} />
          </div>
          <div className="stat-title">Monitoring</div>
          <div className="stat-value text-success">24/7</div>
          <div className="stat-desc">Real-time threat detection</div>
        </div>

        <div className="stat bg-base-100 shadow-lg">
          <div className="stat-figure text-info">
            <Key size={32} />
          </div>
          <div className="stat-title">Access Control</div>
          <div className="stat-value text-info">MFA</div>
          <div className="stat-desc">Multi-factor authentication</div>
        </div>
      </div>

      {/* Security Measures */}
      <div>
        <h3 className="mb-6 text-xl font-semibold">Our Security Measures</h3>
        <div className="space-y-6">
          {securityMeasures.map((measure, index) => (
            <div className="card bg-base-100 shadow-lg" key={index}>
              <div className="card-body">
                <h4 className="card-title mb-4 text-lg">{measure.category}</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {measure.measures.map((item, itemIndex) => (
                    <div className="flex items-start gap-3" key={itemIndex}>
                      <CheckCircle
                        className="mt-1 flex-shrink-0 text-success"
                        size={16}
                      />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div className="card border border-primary/20 bg-primary/5">
        <div className="card-body">
          <h3 className="card-title mb-4 text-primary">
            Security Certifications
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <div className="badge badge-primary">ISO 27001</div>
              <span className="text-sm">Information Security Management</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="badge badge-secondary">SOC 2 Type II</div>
              <span className="text-sm">Service Organization Controls</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="badge badge-success">PCI DSS</div>
              <span className="text-sm">
                Payment Card Industry Data Security
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="badge badge-info">DPA Compliant</div>
              <span className="text-sm">Data Protection Act</span>
            </div>
          </div>
        </div>
      </div>

      {/* Incident Response */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h3 className="card-title mb-4">Data Breach Response</h3>
          <p className="mb-4 text-base-content/80">
            In the unlikely event of a data security incident, we have
            comprehensive procedures in place to protect our users and comply
            with legal requirements.
          </p>

          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-start">Immediate</div>
              <div className="timeline-middle">
                <Bell className="text-error" size={16} />
              </div>
              <div className="timeline-end timeline-box">
                <div className="font-semibold">Detection & Containment</div>
                <div className="text-sm">Incident identified and contained</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-start">Within 24hrs</div>
              <div className="timeline-middle">
                <Users className="text-warning" size={16} />
              </div>
              <div className="timeline-end timeline-box">
                <div className="font-semibold">Assessment & Investigation</div>
                <div className="text-sm">
                  Full scope analysis and risk assessment
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-start">Within 72hrs</div>
              <div className="timeline-middle">
                <Globe className="text-info" size={16} />
              </div>
              <div className="timeline-end timeline-box">
                <div className="font-semibold">Regulatory Notification</div>
                <div className="text-sm">
                  Report to Information Regulator if required
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-start">As needed</div>
              <div className="timeline-middle">
                <Mail className="text-success" size={16} />
              </div>
              <div className="timeline-end timeline-box">
                <div className="font-semibold">User Notification</div>
                <div className="text-sm">
                  Direct communication to affected users
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequests = () => (
    <div className="space-y-8">
      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="card cursor-pointer border border-primary/20 bg-primary/5 transition-shadow hover:shadow-lg">
          <div className="card-body text-center">
            <Download className="mx-auto mb-2 text-primary" size={32} />
            <h4 className="font-semibold">Download My Data</h4>
            <p className="text-sm text-base-content/70">
              Get a copy of all your data
            </p>
          </div>
        </div>

        <div className="card cursor-pointer border border-secondary/20 bg-secondary/5 transition-shadow hover:shadow-lg">
          <div className="card-body text-center">
            <Edit className="mx-auto mb-2 text-secondary" size={32} />
            <h4 className="font-semibold">Update Information</h4>
            <p className="text-sm text-base-content/70">
              Correct your personal data
            </p>
          </div>
        </div>

        <div className="card cursor-pointer border border-error/20 bg-error/5 transition-shadow hover:shadow-lg">
          <div className="card-body text-center">
            <Trash2 className="mx-auto mb-2 text-error" size={32} />
            <h4 className="font-semibold">Delete Account</h4>
            <p className="text-sm text-base-content/70">Remove all your data</p>
          </div>
        </div>

        <div className="card cursor-pointer border border-info/20 bg-info/5 transition-shadow hover:shadow-lg">
          <div className="card-body text-center">
            <Eye className="mx-auto mb-2 text-info" size={32} />
            <h4 className="font-semibold">Privacy Settings</h4>
            <p className="text-sm text-base-content/70">
              Manage your preferences
            </p>
          </div>
        </div>
      </div>

      {/* Data Request Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h3 className="card-title mb-6 text-xl">
            Submit a Data Protection Request
          </h3>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    Request Type <span className="text-error">*</span>
                  </span>
                </label>
                <select
                  className={`select select-bordered ${
                    formErrors.request_type ? "select-error" : ""
                  }`}
                  name="request_type"
                  onChange={handleInputChange}
                  value={formData.request_type}
                >
                  <option value="">Select request type</option>
                  <option value="access_data">Access to my data</option>
                  <option value="data_correction">Data correction</option>
                  <option value="data_deletion">Data deletion</option>
                  <option value="data_portability">Data portability</option>
                  <option value="object_to_processing">
                    Object to processing
                  </option>
                  <option value="restrict_processing">
                    Restrict processing
                  </option>
                </select>
                {formErrors.request_type && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {formErrors.request_type[0]}
                    </span>
                  </label>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    Priority <span className="text-error">*</span>
                  </span>
                </label>
                <select
                  className={`select select-bordered ${
                    formErrors.priority ? "select-error" : ""
                  }`}
                  name="priority"
                  onChange={handleInputChange}
                  value={formData.priority}
                >
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                </select>
                {formErrors.priority && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {formErrors.priority[0]}
                    </span>
                  </label>
                )}
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  Email Address <span className="text-error">*</span>
                </span>
              </label>
              <input
                className={`input input-bordered ${
                  formErrors.email ? "input-error" : ""
                }`}
                name="email"
                onChange={handleInputChange}
                placeholder="Your registered email address"
                type="email"
                value={formData.email}
              />
              {formErrors.email && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {formErrors.email[0]}
                  </span>
                </label>
              )}
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  Phone Number <span className="text-error">*</span>
                </span>
              </label>
              <input
                className={`input input-bordered ${
                  formErrors.phone ? "input-error" : ""
                }`}
                name="phone"
                onChange={handleInputChange}
                placeholder="Your registered phone number"
                type="tel"
                value={formData.phone}
              />
              {formErrors.phone && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {formErrors.phone[0]}
                  </span>
                </label>
              )}
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  Request Details <span className="text-error">*</span>
                </span>
              </label>
              <textarea
                className={`textarea textarea-bordered h-24 ${
                  formErrors.request_details ? "textarea-error" : ""
                }`}
                name="request_details"
                onChange={handleInputChange}
                placeholder="Please provide specific details about your request..."
                value={formData.request_details}
              ></textarea>
              <label className="label">
                <span className="label-text-alt">
                  {formData.request_details.length}/2000 characters
                </span>
                {formErrors.request_details && (
                  <span className="label-text-alt text-error">
                    {formErrors.request_details[0]}
                  </span>
                )}
              </label>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  Identity Verification <span className="text-error">*</span>
                </span>
              </label>
              <input
                accept=".pdf,.jpg,.png"
                className={`file-input file-input-bordered ${
                  formErrors.identity_verification_file
                    ? "file-input-error"
                    : ""
                }`}
                name="identity_verification_file"
                onChange={handleFileChange}
                type="file"
              />
              <div className="label">
                <span className="label-text-alt">
                  Upload ID document for verification (PDF, JPG, PNG - Max 5MB)
                </span>
                {formErrors.identity_verification_file && (
                  <span className="label-text-alt text-error">
                    {formErrors.identity_verification_file[0]}
                  </span>
                )}
              </div>
            </div>

            <div className="form-control">
              <label className="label cursor-pointer">
                <input
                  checked={formData.confirmation}
                  className="checkbox"
                  name="confirmation"
                  onChange={handleInputChange}
                  type="checkbox"
                />
                <span className="label-text ml-2">
                  I confirm that I am the data subject or have authorization to
                  make this request <span className="text-error">*</span>
                </span>
              </label>
              {formErrors.confirmation && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {formErrors.confirmation[0]}
                  </span>
                </label>
              )}
            </div>

            <div className="card-actions">
              <button
                className="btn btn-primary"
                disabled={loading}
                type="submit"
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
              <button
                className="btn btn-ghost"
                disabled={loading}
                onClick={() => {
                  setFormData({
                    confirmation: false,
                    email: "",
                    identity_verification_file: null,
                    phone: "",
                    priority: "normal",
                    request_details: "",
                    request_type: "",
                  });
                  setFormErrors({});
                  setSuccessMessage("");
                  setErrorMessage("");
                  const fileInput = document.querySelector(
                    'input[name="identity_verification_file"]',
                  ) as HTMLInputElement;
                  if (fileInput) {
                    fileInput.value = "";
                  }
                }}
                type="button"
              >
                Clear Form
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Contact Information */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h3 className="card-title mb-4">Data Protection Officer</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="text-primary" size={16} />
                <span>legal@hareeng.org</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="text-primary" size={16} />
                <span>+266 50480814</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="text-primary" size={16} />
                <span>Mon-Fri: 9:00 AM - 5:00 PM SAST</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h3 className="card-title mb-4">Information Regulator</h3>
            <p className="mb-3 text-sm text-base-content/70">
              You have the right to lodge a complaint with the Information
              Regulator if you believe your data protection rights have been
              violated.
            </p>
            <div className="space-y-2">
              <div className="text-sm">
                <strong>Website:</strong> www.lca.org.ls
              </div>
              <div className="text-sm">
                <strong>Email:</strong> complaints@lca.org.ls
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Times */}
      <div className="alert alert-info">
        <Clock size={20} />
        <div>
          <div className="font-semibold">Processing Times</div>
          <div className="text-sm">
            We respond to data protection requests within 30 days as required by
            the Data Protection Act. Complex requests may require additional
            time, and we will notify you if an extension is needed.
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <AppNavbar />

      {/* Success Toast */}
      <div
        className={`fixed right-4 top-4 z-50 transition-all duration-500 ease-out ${
          showSuccessToast
            ? "translate-x-0 translate-y-0 opacity-100"
            : "translate-x-96 translate-y-0 opacity-0"
        }`}
      >
        <div className="rounded-lg border border-success/30 bg-success/10 p-4 shadow-lg backdrop-blur-sm dark:border-success/50 dark:bg-success/20">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <CheckCircle className="text-success" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-success">Success</h3>
              <p className="mt-1 text-sm text-base-content/80">
                {successMessage}
              </p>
            </div>
            <button
              className="flex-shrink-0 text-base-content/50 transition-colors hover:text-base-content"
              onClick={() => setShowSuccessToast(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Error Toast */}
      <div
        className={`fixed right-4 top-4 z-50 transition-all duration-500 ease-out ${
          showErrorToast
            ? "translate-x-0 translate-y-0 opacity-100"
            : "translate-x-96 translate-y-0 opacity-0"
        }`}
      >
        <div className="rounded-lg border border-error/30 bg-error/10 p-4 shadow-lg backdrop-blur-sm dark:border-error/50 dark:bg-error/20">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <AlertCircle className="text-error" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-error">Error</h3>
              <p className="mt-1 text-sm text-base-content/80">
                {errorMessage}
              </p>
            </div>
            <button
              className="flex-shrink-0 text-base-content/50 transition-colors hover:text-base-content"
              onClick={() => setShowErrorToast(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Header */}
        <section className="bg-primary py-16 text-primary-content">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-content/20 px-4 py-2">
                <Shield size={20} />
                <span className="font-medium">
                  Data Protection Act Compliant
                </span>
              </div>

              <h1 className="mb-4 text-4xl font-bold lg:text-5xl">
                Data Protection & Privacy
              </h1>

              <p className="mx-auto max-w-3xl text-xl opacity-90">
                Your privacy matters to us. Learn about how we protect, process,
                and give you control over your personal information in
                compliance with data protection act laws.
              </p>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="border-b bg-base-100">
          <div className="mx-auto max-w-7xl px-4">
            <div className="tabs-boxed tabs bg-transparent p-4">
              {tabs.map((tab) => (
                <button
                  className={`tab gap-2 ${activeTab === tab.id ? "tab-active" : ""}`}
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            {activeTab === "overview" && renderOverview()}
            {activeTab === "your-rights" && renderYourRights()}
            {activeTab === "security" && renderSecurity()}
            {activeTab === "requests" && renderRequests()}
          </div>
        </section>

        {/* Footer Info */}
        <section className="bg-base-100 py-8">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <p className="text-sm text-base-content/70">
                Last updated: January 1, {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
